import { useCallback, useState, useRef } from 'react';
import { makeFieldKey } from '../../../utils/formCrafter/listViewUtils';

/**
 * useListViewFieldEditing Hook
 *
 * Manages field editing state and actions for FormCrafterListView.
 * Handles editing drafts, confirmed values, pending edit tracking,
 * and cascade editing for dependent conditional fields.
 *
 * @param {string|number} formId - Form ID for key generation
 * @param {Function} markPendingEdit - Optional callback when edit starts
 * @param {Function} markResolvedEdit - Optional callback when edit ends
 * @param {Object} formRendererRef - Ref to form renderer for value sync
 * @returns {Object} - Field editing state and actions
 */
const useListViewFieldEditing = ({
  formId,
  markPendingEdit,
  markResolvedEdit,
  formRendererRef,
} = {}) => {
  const [editedValues, setEditedValues] = useState({});
  const [editingDrafts, setEditingDrafts] = useState({});
  // Track cascade root fields -> their dependent fields being edited
  const cascadeRootsRef = useRef(new Map());
  // Track deleted server file IDs per field key (for display filtering)
  const [deletedServerFileIds, setDeletedServerFileIds] = useState({});
  // Track files deleted during current edit session (for rollback on cancel)
  const pendingDeletedFilesRef = useRef({});

  const getFieldKey = useCallback(
    (subformId, fieldId) => makeFieldKey(formId, subformId, fieldId),
    [formId]
  );

  const isEditing = useCallback(
    (subformId, fieldId) => {
      const key = getFieldKey(subformId, fieldId);
      return key
        ? Object.prototype.hasOwnProperty.call(editingDrafts, key)
        : false;
    },
    [getFieldKey, editingDrafts]
  );

  const getEditedValue = useCallback(
    (subformId, fieldId, originalValue) => {
      const key = getFieldKey(subformId, fieldId);
      if (key && Object.prototype.hasOwnProperty.call(editedValues, key)) {
        return editedValues[key];
      }
      return originalValue;
    },
    [getFieldKey, editedValues]
  );

  const getDraftValue = useCallback(
    (subformId, fieldId) => {
      const key = getFieldKey(subformId, fieldId);
      return key ? editingDrafts[key] : undefined;
    },
    [getFieldKey, editingDrafts]
  );

  const startEdit = useCallback(
    (subformId, fieldId, initialValue) => {
      const key = getFieldKey(subformId, fieldId);
      if (!key) return;

      setEditingDrafts((prev) => ({
        ...prev,
        [key]: initialValue ?? '',
      }));

      if (markPendingEdit) {
        markPendingEdit(key);
      }
    },
    [getFieldKey, markPendingEdit]
  );

  const cancelEdit = useCallback(
    (subformId, fieldId) => {
      const key = getFieldKey(subformId, fieldId);
      if (!key) return;

      // Get pending deleted files for rollback (only files deleted in THIS edit session)
      const pendingDeleted = pendingDeletedFilesRef.current[key] || [];

      setEditingDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });

      // Only rollback deletions made during the CURRENT edit session,
      // NOT deletions confirmed in previous sessions.
      // This prevents previously deleted files from reappearing after cancel.
      if (pendingDeleted.length > 0) {
        const pendingSet = new Set(pendingDeleted);

        setDeletedServerFileIds((prev) => {
          if (!prev[key] || prev[key].length === 0) return prev;
          const next = { ...prev };
          // Remove only the IDs that were deleted in this session
          next[key] = prev[key].filter((id) => !pendingSet.has(id));
          if (next[key].length === 0) {
            delete next[key];
          }
          return next;
        });

        // Rollback file deletions in form renderer
        if (formRendererRef?.current?.unmarkDeletedFileIds) {
          formRendererRef.current.unmarkDeletedFileIds(pendingDeleted);
        }
      }

      // Clear pending deleted tracking for this field
      delete pendingDeletedFilesRef.current[key];

      if (markResolvedEdit) {
        markResolvedEdit(key);
      }
    },
    [getFieldKey, markResolvedEdit, formRendererRef]
  );

  const confirmEdit = useCallback(
    (subformId, fieldId) => {
      const key = getFieldKey(subformId, fieldId);
      if (!key) return;

      const draftValue = editingDrafts[key];

      // Sync with form renderer if available
      if (formRendererRef?.current?.setFieldValue) {
        formRendererRef.current.setFieldValue(subformId, fieldId, draftValue);
      }

      // Save confirmed value
      setEditedValues((prev) => ({
        ...prev,
        [key]: draftValue,
      }));

      // Clear draft
      setEditingDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });

      // Clear pending deleted tracking (deletions are now confirmed)
      delete pendingDeletedFilesRef.current[key];

      if (markResolvedEdit) {
        markResolvedEdit(key);
      }
    },
    [getFieldKey, editingDrafts, formRendererRef, markResolvedEdit]
  );

  const updateDraft = useCallback(
    (subformId, fieldId, value) => {
      const key = getFieldKey(subformId, fieldId);
      if (!key) return;

      setEditingDrafts((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [getFieldKey]
  );

  // Reset multiple field values at once (for cascade reset)
  const resetFieldValueBatch = useCallback(
    (fieldsToReset) => {
      if (!fieldsToReset || fieldsToReset.length === 0) return;

      // Set to empty in edited values (not delete, so reset persists when field reappears)
      setEditedValues((prev) => {
        const next = { ...prev };
        let changed = false;
        fieldsToReset.forEach(({ subformId, fieldId }) => {
          const key = getFieldKey(subformId, fieldId);
          if (key) {
            // Set to empty instead of deleting - this ensures the reset value
            // is used when displaying, not the original formData value
            if (next[key] !== '') {
              next[key] = '';
              changed = true;
            }
          }
        });
        return changed ? next : prev;
      });

      // Clear from editing drafts
      setEditingDrafts((prev) => {
        const next = { ...prev };
        let changed = false;
        fieldsToReset.forEach(({ subformId, fieldId }) => {
          const key = getFieldKey(subformId, fieldId);
          if (key && Object.prototype.hasOwnProperty.call(next, key)) {
            delete next[key];
            changed = true;
            if (markResolvedEdit) {
              markResolvedEdit(key);
            }
          }
        });
        return changed ? next : prev;
      });

      // Reset in form renderer if available
      if (formRendererRef?.current?.setFieldValue) {
        fieldsToReset.forEach(({ subformId, fieldId }) => {
          formRendererRef.current.setFieldValue(subformId, fieldId, '');
        });
      }
    },
    [getFieldKey, formRendererRef, markResolvedEdit]
  );

  // Start editing multiple fields at once (for cascade)
  const startEditBatch = useCallback(
    (fieldsToEdit) => {
      if (!fieldsToEdit || fieldsToEdit.length === 0) return;

      setEditingDrafts((prev) => {
        const next = { ...prev };
        fieldsToEdit.forEach(({ subformId, fieldId, initialValue }) => {
          const key = getFieldKey(subformId, fieldId);
          if (key && !Object.prototype.hasOwnProperty.call(next, key)) {
            next[key] = initialValue ?? '';
            if (markPendingEdit) {
              markPendingEdit(key);
            }
          }
        });
        return next;
      });
    },
    [getFieldKey, markPendingEdit]
  );

  // Cancel editing multiple fields at once (for cascade cleanup)
  const cancelEditBatch = useCallback(
    (fieldKeys) => {
      if (!fieldKeys || fieldKeys.length === 0) return;

      setEditingDrafts((prev) => {
        const next = { ...prev };
        fieldKeys.forEach((key) => {
          if (Object.prototype.hasOwnProperty.call(next, key)) {
            delete next[key];
            if (markResolvedEdit) {
              markResolvedEdit(key);
            }
          }
        });
        return next;
      });
    },
    [markResolvedEdit]
  );

  // Register a cascade: root field -> its dependent fields
  const registerCascade = useCallback((rootKey, dependentKeys) => {
    cascadeRootsRef.current.set(rootKey, new Set(dependentKeys));
  }, []);

  // Get dependent keys for a cascade root
  const getCascadeDependents = useCallback((rootKey) => {
    return cascadeRootsRef.current.get(rootKey) || new Set();
  }, []);

  // Check if a field is a cascade root
  const isCascadeRoot = useCallback((key) => {
    return cascadeRootsRef.current.has(key);
  }, []);

  // Clear cascade registration when root editing ends
  const clearCascade = useCallback((rootKey) => {
    cascadeRootsRef.current.delete(rootKey);
  }, []);

  // Get all currently editing field keys
  const getEditingKeys = useCallback(() => {
    return Object.keys(editingDrafts);
  }, [editingDrafts]);

  // Track a server file as deleted for a specific field
  const markServerFileDeleted = useCallback(
    (subformId, fieldId, fileId) => {
      const key = getFieldKey(subformId, fieldId);
      if (!key || !fileId) return;

      // Track for display filtering
      setDeletedServerFileIds((prev) => ({
        ...prev,
        [key]: [...(prev[key] || []), fileId],
      }));

      // Track in pending deletions for rollback on cancel
      if (!pendingDeletedFilesRef.current[key]) {
        pendingDeletedFilesRef.current[key] = [];
      }
      pendingDeletedFilesRef.current[key].push(fileId);
    },
    [getFieldKey]
  );

  // Get deleted server file IDs for a field
  const getDeletedServerFileIds = useCallback(
    (subformId, fieldId) => {
      const key = getFieldKey(subformId, fieldId);
      return key ? deletedServerFileIds[key] || [] : [];
    },
    [getFieldKey, deletedServerFileIds]
  );

  return {
    editedValues,
    editingDrafts,
    deletedServerFileIds,
    getFieldKey,
    isEditing,
    getEditedValue,
    getDraftValue,
    startEdit,
    cancelEdit,
    confirmEdit,
    updateDraft,
    resetFieldValueBatch,
    startEditBatch,
    cancelEditBatch,
    registerCascade,
    getCascadeDependents,
    isCascadeRoot,
    clearCascade,
    getEditingKeys,
    markServerFileDeleted,
    getDeletedServerFileIds,
  };
};

export default useListViewFieldEditing;
